// =============================================================================
// src/views/demografica/demografica-recorte-data.ts
//
// Dados e mapeamentos do recorte demografico (Tela 04 · Analise Demografica).
//
// Proveniencia: assets/unified-app.js Section 4.4 (Sections.demografica),
//   linhas 1282-1288 (demoData) e 1356 (recorteMap). Valores preservados 1:1.
//
// Regra Clean Code: tipos explicitos, nomes greppaveis (prefixo DEMOGRAFICA_),
//   zero `any`. Aqui mora SO o dado — a logica vive em demografica-sync.ts.
// =============================================================================

import type { ZonaKey } from '../../app/types';

/**
 * Recortes socioeconomicos que possuem mapa de concentracao por zona.
 * Espelha as chaves de `demoData` no original (renda/escolaridade/religiao/genero).
 */
export type DemograficaRecorte =
  | 'renda'
  | 'escolaridade'
  | 'religiao'
  | 'genero';

/** Grupos de chip do drawer "editar recorte" (atributo data-g no markup). */
export type DemograficaGrupoChip =
  | 'gender'
  | 'age'
  | 'edu'
  | 'income'
  | 'race'
  | 'rel';

/** Concentracao [0..1] do recorte por zona eleitoral de Manaus. */
export type DemograficaConcentracaoPorZona = Record<ZonaKey, number>;

/**
 * Concentracao do eleitorado por recorte e zona. Modula a opacidade de
 * preenchimento no mapa (ver demografica-sync._applyStyles).
 *
 * Proveniencia: assets/unified-app.js linhas 1284-1287, valores intactos.
 */
export const DEMOGRAFICA_CONCENTRACAO: Record<
  DemograficaRecorte,
  DemograficaConcentracaoPorZona
> = {
  renda: { norte: 0.45, leste: 0.52, 'centro-oeste': 0.58, 'centro-sul': 0.82, oeste: 0.35, sul: 0.68 },
  escolaridade: { norte: 0.5, leste: 0.48, 'centro-oeste': 0.55, 'centro-sul': 0.8, oeste: 0.38, sul: 0.62 },
  religiao: { norte: 0.72, leste: 0.68, 'centro-oeste': 0.65, 'centro-sul': 0.45, oeste: 0.78, sul: 0.6 },
  genero: { norte: 0.52, leste: 0.51, 'centro-oeste': 0.53, 'centro-sul': 0.49, oeste: 0.54, sul: 0.5 },
};

/**
 * Grupo do chip selecionado -> recorte demografico aplicado ao mapa.
 *
 * Proveniencia: assets/unified-app.js linha 1356. Nota de fidelidade: `race`
 *   mapeia para `renda` e `age` para `escolaridade` no original (reaproveitamento
 *   intencional de paleta) — replicado exatamente, nao "corrigido".
 */
export const DEMOGRAFICA_RECORTE_POR_GRUPO: Record<
  DemograficaGrupoChip,
  DemograficaRecorte
> = {
  income: 'renda',
  edu: 'escolaridade',
  rel: 'religiao',
  gender: 'genero',
  race: 'renda',
  age: 'escolaridade',
};

/** Recorte default quando nao ha chips selecionados (original cai em 'renda'). */
export const DEMOGRAFICA_RECORTE_DEFAULT: DemograficaRecorte = 'renda';

/** Concentracao de fallback por zona ausente no mapa (original: `|| 0.5`). */
export const DEMOGRAFICA_CONCENTRACAO_FALLBACK = 0.5;
